import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FilterableSelect } from "@/components/ui/filterable-select"
import {
  fetchInvoiceCounterparties,
  updateInvoiceCounterparty,
  type CounterpartySummary
} from "@/services/invoiceService"
import { useToast } from "@/hooks/use-toast"
import type { CounterpartyMetadata, ExtractedField, Invoice } from "../types"
import type { EditableValueRenderer } from "../utils/invoiceFieldHelpers"
import { OriginalValueIndicator } from "./OriginalValueIndicator"
import { Lock } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

interface VendorCounterpartyFieldProps {
  invoice: Invoice
  field: ExtractedField
  onFieldUpdate?: (fieldId: string, newValue: string) => void
  onInvoiceRefresh?: () => void
  fieldPrefix?: "vendor" | "customer"
  renderEditableValue: EditableValueRenderer
  readOnly?: boolean
  exportLocked?: boolean
}

export const VendorCounterpartyField = ({
  invoice,
  field,
  onFieldUpdate,
  onInvoiceRefresh,
  fieldPrefix = "vendor",
  renderEditableValue,
  readOnly = false,
  exportLocked = false
}: VendorCounterpartyFieldProps): JSX.Element => {
  const { toast } = useToast()

  const counterpartyMetadata = invoice.additional_metadata?.counterparty as CounterpartyMetadata | undefined

  const resolveInitialCounterpartyId = useCallback(() => {
    const mirageCounterpartyId = invoice.mirage_counterparty_id
    if (mirageCounterpartyId != null) return mirageCounterpartyId.toString()

    const metaSelected = counterpartyMetadata?.selected_candidate?.id
    if (metaSelected != null) return metaSelected.toString()

    const metaAuto = counterpartyMetadata?.auto_candidate?.id
    if (metaAuto != null) return metaAuto.toString()

    return "none"
  }, [invoice.mirage_counterparty_id, counterpartyMetadata])

  const [selectedCounterpartyId, setSelectedCounterpartyId] = useState<string>(resolveInitialCounterpartyId)
  const [isCounterpartyUpdating, setIsCounterpartyUpdating] = useState(false)
  const [counterpartyOptions, setCounterpartyOptions] = useState<CounterpartySummary[]>([])
  const [counterpartyLoading, setCounterpartyLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const originalFieldValuesRef = useRef<Record<string, string | null>>({})

  const fieldMap = useMemo(() => {
    const map: Record<string, ExtractedField> = {}
    invoice.extractedData?.forEach((item) => {
      map[item.field_name.toLowerCase()] = item
    })
    return map
  }, [invoice.extractedData])

  useEffect(() => {
    originalFieldValuesRef.current = {}

    invoice.extractedData?.forEach((extractedField) => {
      const key = extractedField.field_name.toLowerCase()
      if (
        (key === `${fieldPrefix}_name` ||
          key === `${fieldPrefix}_address` ||
          key === `${fieldPrefix}_tax_id` ||
          key === `${fieldPrefix}_country`) &&
        !(key in originalFieldValuesRef.current)
      ) {
        originalFieldValuesRef.current[key] = extractedField.original_value ?? extractedField.field_value ?? null
      }
    })
  }, [invoice.id, invoice.extractedData, fieldPrefix])

  useEffect(() => {
    setSelectedCounterpartyId(resolveInitialCounterpartyId())
  }, [resolveInitialCounterpartyId])

  useEffect(() => {
    let isCancelled = false

    async function loadCounterparties() {
      const invoiceId = invoice.id
      const invoiceClientId = invoice.client?.id

      if (!invoiceId || !invoiceClientId) {
        if (!isCancelled) {
          setCounterpartyOptions([])
          setSelectedCounterpartyId("none")
        }
        return
      }

      setCounterpartyLoading(true)
      try {
        const options = await fetchInvoiceCounterparties(invoiceId)
        if (isCancelled) return
        setCounterpartyOptions(options)
        setSelectedCounterpartyId(resolveInitialCounterpartyId())
      } catch (error) {
        if (isCancelled) return
        setCounterpartyOptions([])
        toast({
          title: "Failed to load counterparties",
          description: error instanceof Error ? error.message : "Unable to load counterparty list",
          variant: "destructive"
        })
      } finally {
        if (!isCancelled) {
          setCounterpartyLoading(false)
        }
      }
    }

    loadCounterparties()

    return () => {
      isCancelled = true
    }
  }, [invoice.id, invoice.client?.id, invoice.mirage_counterparty_id, toast, resolveInitialCounterpartyId])

  useEffect(() => {
    if (!isOpen) return
    const frame = window.requestAnimationFrame(() => {
      // FilterableSelect handles its own search focus; keep hook for consistency
    })
    return () => window.cancelAnimationFrame(frame)
  }, [isOpen])

  const handleCounterpartyChange = useCallback(
    async (value: string) => {
      if (!invoice.id) {
        return
      }

      if (value === selectedCounterpartyId) {
        return
      }

      const candidate = value !== "none"
        ? counterpartyOptions.find((c) => c.id.toString() === value) ?? null
        : null

      const nextCounterpartyId = value === "none" ? null : Number(value)
      if (value !== "none" && Number.isNaN(nextCounterpartyId)) {
        toast({
          title: "Invalid counterparty selection",
          description: "Please choose a valid counterparty option.",
          variant: "destructive"
        })
        return
      }

      const previousValue = selectedCounterpartyId
      setSelectedCounterpartyId(value)
      setIsCounterpartyUpdating(true)

      try {
        const result = await updateInvoiceCounterparty(invoice.id, nextCounterpartyId)

        const updateFieldIfNecessary = async (fieldName: string, nextValue: string | null | undefined) => {
          if (!onFieldUpdate) {
            return
          }

          const lowerCaseName = fieldName.toLowerCase()
          const fieldToUpdate = fieldMap[lowerCaseName]
          if (!fieldToUpdate) {
            return
          }

          const normalizedNext = nextValue ?? ""
          const currentValue = fieldToUpdate.field_value ?? ""

          if (normalizedNext === currentValue) {
            return
          }

          await Promise.resolve(onFieldUpdate(fieldToUpdate.id, normalizedNext))
        }

        if (value === "none") {
          await updateFieldIfNecessary(
            `${fieldPrefix}_name`,
            originalFieldValuesRef.current[`${fieldPrefix}_name`]
          )
          await updateFieldIfNecessary(
            `${fieldPrefix}_address`,
            originalFieldValuesRef.current[`${fieldPrefix}_address`]
          )
          await updateFieldIfNecessary(
            `${fieldPrefix}_tax_id`,
            originalFieldValuesRef.current[`${fieldPrefix}_tax_id`]
          )
          await updateFieldIfNecessary(
            `${fieldPrefix}_country`,
            originalFieldValuesRef.current[`${fieldPrefix}_country`]
          )
        } else {
          const effectiveCounterparty = candidate ?? result.counterparty ?? null
          const counterpartyName =
            effectiveCounterparty?.name?.trim() ||
            effectiveCounterparty?.name_lat?.trim() ||
            effectiveCounterparty?.code?.trim()
          const counterpartyAddress =
            effectiveCounterparty?.address?.trim() || effectiveCounterparty?.address_lat?.trim()
          const counterpartyTaxId = effectiveCounterparty?.vat_code?.trim()

          if (counterpartyName) {
            await updateFieldIfNecessary(`${fieldPrefix}_name`, counterpartyName)
          }

          if (counterpartyAddress) {
            await updateFieldIfNecessary(`${fieldPrefix}_address`, counterpartyAddress)
          }

          if (counterpartyTaxId) {
            await updateFieldIfNecessary(`${fieldPrefix}_tax_id`, counterpartyTaxId)
          }

          const counterpartyCountry = effectiveCounterparty?.country?.trim() || ""
          await updateFieldIfNecessary(`${fieldPrefix}_country`, counterpartyCountry)
        }

        await onInvoiceRefresh?.()
        toast({
          title: "Counterparty updated",
          description: `The invoice ${fieldPrefix} has been updated successfully.`
        })
      } catch (error) {
        setSelectedCounterpartyId(previousValue)
        toast({
          title: "Failed to update counterparty",
          description: error instanceof Error ? error.message : "Unable to update counterparty",
          variant: "destructive"
        })
      } finally {
        setIsCounterpartyUpdating(false)
      }
    },
    [
      invoice.id,
      counterpartyOptions,
      onFieldUpdate,
      onInvoiceRefresh,
      selectedCounterpartyId,
      toast,
      fieldMap,
      fieldPrefix
    ]
  )

  const selectionDisabled = !invoice.client

  return (
    <div className="w-full space-y-3">
      <FilterableSelect
        value={selectedCounterpartyId}
        options={counterpartyOptions.map((candidate) => {
          const label = candidate.name || candidate.code || `ID ${candidate.id}`
          const details = [candidate.vat_code, candidate.country].filter(Boolean).join(" • ")
          return {
            value: candidate.id.toString(),
            label,
            details
          }
        })}
        onChange={handleCounterpartyChange}
        open={isOpen}
        onOpenChange={setIsOpen}
        disabled={selectionDisabled || counterpartyLoading || isCounterpartyUpdating || exportLocked}
        loading={counterpartyLoading}
        placeholder={counterpartyLoading ? "Loading counterparties..." : "Select counterparty"}
        searchPlaceholder="Search counterparties..."
        clearOptionLabel="No counterparty"
        showClearOption
        tooltip="Selecting a counterparty updates address and tax ID from Mirage. Clearing restores the extracted values."
        maxVisible={30}
      />

      <div
        key={field.id}
        aria-invalid={
          invoice.mirage_counterparty_id == null && !(field.field_value ?? "").toString().trim()
            ? true
            : undefined
        }
        className={`items-center justify-end px-0 py-1 self-stretch w-full flex-[0_0_auto] border-b border-neutral-200 flex ${
          invoice.mirage_counterparty_id == null && !(field.field_value ?? "").toString().trim()
            ? "border-l-2 border-l-red-500 pl-2"
            : ""
        }`}
      >
        <div className="flex flex-col items-start flex-1 max-w-full min-w-0">
          <div className="flex items-start w-full">
            <div className="flex flex-1 min-w-0 items-center 2xl:flex-col 2xl:items-start">
              <span className="order-2 h-5 truncate text-sm font-normal text-neutral-500 leading-5 whitespace-nowrap ml-4 capitalize 2xl:order-1 2xl:ml-0 2xl:w-full 2xl:whitespace-normal 2xl:overflow-visible 2xl:text-xs">
                {field.field_name.replace(/_/g, " ")}
                {invoice.mirage_counterparty_id == null && (
                  <span
                    className={`ml-1 ${
                      (field.field_value ?? "").toString().trim() === ""
                        ? "text-red-500"
                        : "text-neutral-400"
                    }`}
                  >
                    *
                  </span>
                )}
              </span>
              <div className="order-1 flex-1 min-w-0 2xl:order-2 w-full flex items-center gap-1">
                {readOnly ? (
                  <div className="flex items-center gap-1 w-full min-w-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Lock className="h-3 w-3 flex-shrink-0 text-neutral-400" />
                      </TooltipTrigger>
                      <TooltipContent>{invoice.status === "exported" ? "Locked — invoice is exported" : "Synced from Mirage"}</TooltipContent>
                    </Tooltip>
                    <span className="min-w-0 max-w-full text-left text-sm font-semibold text-neutral-950 leading-5 break-words whitespace-normal rounded px-1 py-1">
                      {field.field_value || <span className="text-gray-400 italic">{readOnly ? "Not Available" : "No value"}</span>}
                    </span>
                  </div>
                ) : (
                  <>
                    {renderEditableValue(field, {
                      buttonClassName: "flex-1 2xl:w-full",
                      containerClassName: "flex-1 gap-2 2xl:flex-col 2xl:items-stretch"
                    })}
                    <OriginalValueIndicator field={field} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
